// STRALCIO GOVERNANCE · card Panoramica "Variazioni da verificare" + drawer review.
// Governance NON bloccante: nessun effetto su SAL/QE/scope. Read-only per il sub-impact.
import React, { useState, useEffect, useCallback } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { variationReviewApi } from '../../lib/api';
import { toast } from 'sonner';
import { fmtQty, fmtEur } from '../../lib/formatQty';
import { AlertTriangle, CheckCircle2, MessageSquare, ClipboardCheck, Loader2, ShieldAlert } from 'lucide-react';

const STATUS_LABEL = { da_verificare: 'Da verificare', richieste_informazioni: 'Info richieste', verificato: 'Verificato' };
const STATUS_CLS = { da_verificare: 'bg-amber-100 text-amber-700', richieste_informazioni: 'bg-blue-100 text-blue-700', verificato: 'bg-emerald-100 text-emerald-700' };
const SUB_IMPACT = {
  non_affidata: { label: 'Non affidata', cls: 'bg-slate-100 text-slate-600' },
  offerta_da_revisionare: { label: 'Offerta da revisionare', cls: 'bg-amber-100 text-amber-700' },
  subappalto_da_riallineare: { label: 'Subappalto da riallineare', cls: 'bg-orange-100 text-orange-700' },
  bloccato_da_sal: { label: 'Bloccato da SAL', cls: 'bg-rose-100 text-rose-700' },
  allineato: { label: 'Allineato', cls: 'bg-emerald-100 text-emerald-700' },
};
const fmtDT = (s) => { try { return new Date(s).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return s || '—'; } };

export const VariationReviewCard = ({ projectId, canManage, onChanged }) => {
  const [data, setData] = useState(null);
  const [openReview, setOpenReview] = useState(null);

  const load = useCallback(async () => {
    try { const res = await variationReviewApi.list(projectId); setData(res.data); }
    catch { setData(null); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  const open = (data.reviews || []).filter((r) => r.review_status !== 'verificato');

  if (open.length === 0) {
    return (
      <div className="rounded-xl border border-[#E4E4E4] bg-white shadow-sm p-4 h-full flex items-center gap-2 text-sm text-emerald-700" data-testid="variation-review-empty">
        <CheckCircle2 className="w-4 h-4 shrink-0" /> Tutto verificato
      </div>
    );
  }
  const top = open.slice(0, 3);
  const extra = open.length - top.length;
  return (
    <>
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 shadow-sm p-4 h-full flex flex-col" data-testid="variation-review-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-600" />
            <span className="text-[15px] font-semibold text-[#3C3C3C]">Variazioni da verificare</span>
          </div>
          <Badge className="bg-amber-600 text-white" data-testid="variation-review-count">{open.length}</Badge>
        </div>
        <div className="mt-1 text-xs text-[#737373]">
          Valore complessivo <strong className="text-red-600">{fmtEur(data.open_value)}</strong>
        </div>
        <div className="mt-2 space-y-1.5 flex-1">
          {top.map((r) => (
            <div key={r.contract_variation_id} className="flex items-center justify-between gap-2 bg-white rounded-lg border border-[#eee] px-2.5 py-1.5" data-testid={`variation-review-item-${r.contract_variation_id}`}>
              <div className="text-xs min-w-0">
                <div className="font-medium text-[#3C3C3C] truncate">{r.item_code} · {r.measurement_row_description || r.item_description}</div>
                <div className="text-[#a3a3a3]">{fmtQty(r.quantity_removed)} {r.unit} · <span className="text-red-600">{fmtEur(r.value)}</span></div>
              </div>
              <Button size="sm" className="h-7 bg-[#FF7819] hover:bg-[#e56a13] shrink-0" onClick={() => setOpenReview(r)} data-testid="variation-review-verify-btn">Verifica</Button>
            </div>
          ))}
        </div>
        {extra > 0 && (
          <button className="mt-2 text-xs text-[#0078C8] hover:underline text-left" onClick={() => setOpenReview(top[0])} data-testid="variation-review-more">
            + {extra} {extra === 1 ? 'altra variazione' : 'altre variazioni'} · Vedi tutte
          </button>
        )}
      </div>
      <VariationReviewDrawer
        review={openReview} allOpen={open} canManage={canManage}
        onOpenChange={(v) => { if (!v) setOpenReview(null); }}
        onChanged={() => { load(); onChanged && onChanged(); }}
        onSelect={(r) => setOpenReview(r)}
      />
    </>
  );
};

const VariationReviewDrawer = ({ review, allOpen, canManage, onOpenChange, onChanged, onSelect }) => {
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setMsg(''); }, [review?.contract_variation_id]);
  if (!review) return null;
  const sub = SUB_IMPACT[review.sub_impact] || { label: review.sub_impact, cls: 'bg-slate-100 text-slate-600' };

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); onChanged && onChanged(); onOpenChange(false); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
    finally { setBusy(false); }
  };
  const doRequestInfo = () => { if (msg.trim().length < 3) return toast.error('Inserisci un messaggio'); act(() => variationReviewApi.requestInfo(review.contract_variation_id, msg.trim()), 'Richiesta informazioni inviata'); };
  const doReply = () => { if (msg.trim().length < 3) return toast.error('Inserisci un messaggio'); act(() => variationReviewApi.reply(review.contract_variation_id, msg.trim()), 'Risposta inviata'); };
  const doVerify = () => act(() => variationReviewApi.verify(review.contract_variation_id), 'Variazione verificata');

  return (
    <Sheet open={!!review} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="variation-review-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-[#3C3C3C]">
            <ClipboardCheck className="w-4 h-4 text-[#FF7819]" /> Verifica stralcio
            <span className={`ml-1 px-2 py-0.5 rounded text-[10px] font-medium ${STATUS_CLS[review.review_status]}`} data-testid="review-status-badge">{STATUS_LABEL[review.review_status]}</span>
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-4 text-sm">
          <Section title="Lavorazione">
            <Row k="Codice" v={review.item_code} />
            <Row k="Descrizione" v={review.item_description} />
            {review.measurement_row_description && <Row k="Localizzazione" v={review.measurement_row_description} />}
          </Section>
          <Section title="Variazione">
            <Row k="Quantità prima" v={`${fmtQty(review.quantity_before)} ${review.unit || ''}`} />
            <Row k="Quantità stralciata" v={`${fmtQty(review.quantity_removed)} ${review.unit || ''}`} />
            <Row k="Quantità dopo" v={`${fmtQty(review.quantity_after)} ${review.unit || ''}`} />
            <Row k="Prezzo unitario" v={fmtEur(review.unit_price)} />
            <Row k="Valore stralcio" v={<span className="text-red-600 font-semibold">{fmtEur(review.value)}</span>} />
          </Section>
          <Section title="Contesto">
            <Row k="Motivazione" v={review.reason} />
            <Row k="Autore" v={review.author_name} />
            <Row k="Data/ora" v={fmtDT(review.created_at)} />
          </Section>
          <Section title="Impatto subappalto (read-only)">
            <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${sub.cls}`} data-testid="review-sub-impact">{sub.label}</span>
          </Section>
          <Section title="Thread">
            {(review.review_thread || []).length === 0 && <div className="text-xs text-[#a3a3a3]">Nessun messaggio</div>}
            <div className="space-y-2">
              {(review.review_thread || []).map((e, i) => (
                <div key={i} className={`rounded-lg p-2 text-xs ${e.kind === 'request' ? 'bg-blue-50' : 'bg-slate-50'}`} data-testid={`review-thread-${i}`}>
                  <div className="flex items-center gap-1 text-[10px] text-[#a3a3a3]">
                    {e.kind === 'request' ? <MessageSquare className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                    {e.author_name} · {e.author_role} · {fmtDT(e.at)}
                  </div>
                  <div className="text-[#3C3C3C] mt-0.5">{e.message}</div>
                </div>
              ))}
            </div>
          </Section>

          {review.review_status !== 'verificato' && (
            <div className="space-y-2 border-t border-[#eee] pt-3">
              <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={2} className="input-abitalo text-sm"
                placeholder="Messaggio (richiesta o risposta)…" data-testid="review-message-input" />
              <div className="flex flex-wrap gap-2">
                {canManage && (
                  <>
                    <Button size="sm" variant="outline" disabled={busy} onClick={doRequestInfo} data-testid="review-request-info-btn">
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <MessageSquare className="w-3.5 h-3.5 mr-1" />} Richiedi informazioni
                    </Button>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={doVerify} data-testid="review-verify-action-btn">
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />} Segna verificato
                    </Button>
                  </>
                )}
                <Button size="sm" variant="outline" disabled={busy} onClick={doReply} data-testid="review-reply-btn">
                  <MessageSquare className="w-3.5 h-3.5 mr-1" /> Rispondi
                </Button>
              </div>
            </div>
          )}
          {review.review_status === 'verificato' && (
            <div className="text-xs text-emerald-700 bg-emerald-50 rounded p-2">Verificato il {fmtDT(review.reviewed_at)}. Storico conservato.</div>
          )}

          {allOpen && allOpen.length > 1 && (
            <div className="border-t border-[#eee] pt-3">
              <div className="text-[11px] text-[#a3a3a3] mb-1">Altre da verificare</div>
              {allOpen.filter((r) => r.contract_variation_id !== review.contract_variation_id).map((r) => (
                <button key={r.contract_variation_id} onClick={() => onSelect(r)} className="block w-full text-left text-xs py-1 hover:text-[#0078C8]">
                  {r.item_code} · {r.measurement_row_description || r.item_description} · <span className="text-red-600">{fmtEur(r.value)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const Section = ({ title, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wide text-[#a3a3a3] mb-1">{title}</div>
    <div className="space-y-1">{children}</div>
  </div>
);
const Row = ({ k, v }) => (
  <div className="flex items-start justify-between gap-3 text-sm">
    <span className="text-[#737373] shrink-0">{k}</span>
    <span className="text-[#3C3C3C] text-right">{v || '—'}</span>
  </div>
);

export default VariationReviewCard;
